import { type FC, useEffect, useRef, useState } from "react";
import SignaturePadLib from "signature_pad";
import { Button, Checkbox, Label } from "flowbite-react";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import { uploadSignatureToStorage } from "../../utils/signatureStorage";

interface SignaturePadProps {
  onSave: (signaturePath: string, dataUrl: string) => void;
  onCancel: () => void;
}

const SignaturePad: FC<SignaturePadProps> = ({ onSave, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pad, setPad] = useState<SignaturePadLib | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Set canvas dimensions
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d")?.scale(ratio, ratio);

    // Initialize signature pad with transparent background
    const signaturePad = new SignaturePadLib(canvas, {
      backgroundColor: "rgba(0, 0, 0, 0)", // Transparent background
    });

    setPad(signaturePad);

    return () => {
      signaturePad.off();
    };
  }, []);

  const handleSave = async () => {
    if (!pad || pad.isEmpty()) return;
    if (!consentChecked) {
      alert("Bitte stimmen Sie der Datenschutzerklärung zu, um fortzufahren.");
      return;
    }

    setIsUploading(true);

    try {
      // Get PNG data as data URL
      const dataUrl = pad.toDataURL("image/png");
      const base64Data = dataUrl.split(",")[1];

      if (!base64Data) {
        throw new Error("Fehler beim Generieren der Unterschriftsdaten");
      }

      // Upload to storage and get path
      const signaturePath = await uploadSignatureToStorage(base64Data);

      console.log("Signature uploaded, path:", signaturePath);
      console.log("Calling onSave callback...");

      // Return both path and data URL for caching
      onSave(signaturePath, dataUrl);

      console.log("onSave callback completed");
    } catch (error) {
      console.error("Error uploading signature:", error);
      alert("Fehler beim Hochladen der Unterschrift. Bitte versuchen Sie es erneut.");
      setIsUploading(false); // Reset loading state on error
    }
    // Note: Don't reset isUploading on success - component will unmount
  };

  const handleClear = () => {
    if (!pad) return;
    pad.clear();
  };

  const openPrivacyPolicy = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPrivacyPolicy(true);
  };

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
        <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Unterschreiben
          </h3>
          <div className="mb-4 rounded border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-700">
            <canvas ref={canvasRef} className="h-64 w-full touch-none" />
          </div>

          <div className="mb-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="privacy-consent"
                checked={consentChecked}
                onChange={() => setConsentChecked(!consentChecked)}
                disabled={isUploading}
              />
              <Label
                htmlFor="privacy-consent"
                className="flex items-center gap-1"
              >
                <span>Ich habe die</span>
                <button
                  onClick={openPrivacyPolicy}
                  className="font-medium text-blue-600 hover:underline dark:text-blue-500"
                  disabled={isUploading}
                >
                  Datenschutzerklärung
                </button>
                <span>gelesen und bin damit einverstanden.</span>
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button color="light" onClick={handleClear} disabled={isUploading}>
              Löschen
            </Button>
            <Button color="light" onClick={onCancel} disabled={isUploading}>
              Abbrechen
            </Button>
            <Button
              color="primary"
              onClick={handleSave}
              disabled={!consentChecked || isUploading}
            >
              {isUploading ? "Wird hochgeladen..." : "Unterschrift speichern"}
            </Button>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal with higher z-index to appear in front */}
      <PrivacyPolicyModal
        show={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />
    </>
  );
};

export default SignaturePad;
